import { CloudAnomalyAlert } from "../../types/alert";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../ui/common-ui/card";
import { format, subMinutes, subHours, subDays, subYears } from 'date-fns';
import { useMemo } from 'react';
import { Activity, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import AlertTimelineComposedChart from '../../../Cloud/AlertTimelineComposedChart';

interface AlertsTimelineProps {
  alerts: CloudAnomalyAlert[];
  timeRange: string;
}

export const AlertsTimeline = ({ alerts, timeRange }: AlertsTimelineProps) => {

  const { timelineData, stats } = useMemo(() => {
    const now = new Date();
    let startTime: Date;
    let interval: number;
    let formatPattern: string;

    switch (timeRange) {
      case "30m": // Last 30 minutes
        startTime = subMinutes(now, 30);
        interval = 2;
        formatPattern = 'HH:mm';
        break;
      case "1h": // Last 1 hour
        startTime = subHours(now, 1);
        interval = 5;
        formatPattern = 'HH:mm';
        break;
      case "4h": // Last 4 hours
        startTime = subHours(now, 4);
        interval = 15;
        formatPattern = 'HH:mm';
        break;
      case "12h": // Last 12 hours
        startTime = subHours(now, 12);
        interval = 30;
        formatPattern = 'HH:mm';
        break;
      case "24h": // Last 24 hours
        startTime = subHours(now, 24);
        interval = 60;
        formatPattern = 'HH:mm';
        break;
      case "7d": // Last 7 days
        startTime = subDays(now, 7);
        interval = 360;
        formatPattern = 'MMM dd';
        break;
      case "30d": // Last 30 days
        startTime = subDays(now, 30);
        interval = 1440;
        formatPattern = 'MMM dd';
        break;
      case "60d": // Last 60 days
        startTime = subDays(now, 60);
        interval = 2880;
        formatPattern = 'MMM dd';
        break;
      case "1y": // Last 1 year
        startTime = subYears(now, 1);
        interval = 10080;
        formatPattern = 'MMM yyyy';
        break;
      default:
        startTime = subHours(now, 24);
        interval = 60;
        formatPattern = 'HH:mm';
    }

    const filteredAlerts = alerts.filter(alert =>
      new Date(alert.triggeredAt || alert.createdAt) >= startTime
    );

    const timeSlots: Record<string, { total: number; critical: number; high: number; medium: number; low: number }> = {};
    let currentTime = startTime;

    while (currentTime <= now) {
      const slotKey = currentTime.toISOString();
      timeSlots[slotKey] = { total: 0, critical: 0, high: 0, medium: 0, low: 0 };
      currentTime = new Date(currentTime.getTime() + interval * 60000);
    }

    filteredAlerts.forEach(alert => {
      const alertTime = new Date(alert.triggeredAt || alert.createdAt);
      let bestSlot = startTime;

      for (const slot of Object.keys(timeSlots)) {
        const slotTime = new Date(slot);
        if (slotTime <= alertTime && slotTime > bestSlot) {
          bestSlot = slotTime;
        }
      }

      const slotKey = bestSlot.toISOString();
      if (timeSlots[slotKey] !== undefined) {
        timeSlots[slotKey].total++;
        if (alert.severity === 'Critical') timeSlots[slotKey].critical++;
        else if (alert.severity === 'High') timeSlots[slotKey].high++;
        else if (alert.severity === 'Medium') timeSlots[slotKey].medium++;
      }
    });

    const data = Object.entries(timeSlots)
      .map(([time, counts]) => ({
        time,
        ...counts,
        formattedTime: format(new Date(time), formatPattern)
      }))
      .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

    // Calculate stats
    const totalAlerts = filteredAlerts.length;
    const criticalCount = filteredAlerts.filter(a => a.severity === 'Critical').length;
    const highCount = filteredAlerts.filter(a => a.severity === 'High').length;

    // Calculate trend (compare first half vs second half)
    const midPoint = Math.floor(data.length / 2);
    const firstHalf = data.slice(0, midPoint).reduce((sum, d) => sum + d.total, 0);
    const secondHalf = data.slice(midPoint).reduce((sum, d) => sum + d.total, 0);
    const trend = firstHalf === 0 ? 0 : ((secondHalf - firstHalf) / firstHalf) * 100;

    return {
      timelineData: data,
      stats: { totalAlerts, criticalCount, highCount, trend }
    };
  }, [alerts, timeRange]);

  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case "30m": return "Last 30 min";
      case "1h": return "Last 1 hour";
      case "4h": return "Last 4 hours";
      case "12h": return "Last 12 hours";
      case "24h": return "Last 24 hours";
      case "7d": return "Last 7 days";
      case "30d": return "Last 30 days";
      case "60d": return "Last 60 days";
      case "1y": return "Last 1 year";
      default: return "Last 24 hours";
    }
  };

  const TrendIcon = stats.trend > 5 ? TrendingUp : stats.trend < -5 ? TrendingDown : Minus;
  const trendColor = stats.trend > 5 ? 'text-red-500' : stats.trend < -5 ? 'text-green-500' : 'text-muted-foreground';

  return (
    <Card className="rounded-2xl border portal-card-elevated bg-card overflow-hidden">
      <CardHeader className="pb-2 pt-5 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Activity className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold text-foreground">
                Alert Timeline
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {getTimeRangeLabel()}
              </p>
            </div>
          </div>

          {/* Stats pills */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-lg">
              <span className="text-xs text-muted-foreground">Total</span>
              <span className="text-sm font-bold text-foreground">{stats.totalAlerts}</span>
            </div>
            {stats.criticalCount > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
                <span className="text-xs text-red-600">Critical</span>
                <span className="text-sm font-bold text-red-600">{stats.criticalCount}</span>
              </div>
            )}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 bg-muted/30 rounded-lg ${trendColor}`}>
              <TrendIcon className="h-4 w-4" />
              <span className="text-xs font-medium">
                {stats.trend > 0 ? '+' : ''}{stats.trend.toFixed(0)}%
              </span>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-4 pb-6 px-6">
        <AlertTimelineComposedChart data={timelineData} />

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-border">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-primary/80" />
            <span className="text-xs text-muted-foreground">Alert Count</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 bg-primary rounded" />
            <span className="text-xs text-muted-foreground">Trend</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
